import React, { useState } from 'react';
import { useGame } from '../context/GameContext';
import CardHand from '../components/CardHand';
import PlayerList from '../components/PlayerList';
import WinnerScreen from '../components/WinnerScreen';
import RemovedCardsHistory from '../components/RemovedCardsHistory';
import DiscardPile from '../components/DiscardPile';
import { getGameType } from '../utils/gameTypes';
import { canPlayCard, getPlayableCards } from '../utils/unoGameLogic';
import './GamePage.css';

/**
 * Main game page - Supports multiple game types
 */
const GamePage = () => {
  const { 
    game, 
    currentPlayer, 
    potBall, 
    undoRemove,
    canUndo,
    resetGame, 
    leaveGame, 
    loading,
    error,
    getCurrentPlayerData,
    playUnoCard,
    drawUnoCard,
    callUno,
    playKittensCard,
    drawKittensCard,
    placeExplodingKitten
  } = useGame();
  
  const [isRemoving, setIsRemoving] = useState(false);
  const [selectedWildColor, setSelectedWildColor] = useState(null);
  const [localError, setLocalError] = useState(null);
  
  const gameTypeId = game?.gameType || 'billiards';
  const gameType = getGameType(gameTypeId);
  const isUno = gameTypeId === 'uno';
  const isKittens = gameTypeId === 'explodingKittens';

  if (!game || !game.players) return null;

  const playerData = getCurrentPlayerData();
  const hasCards = playerData?.cards?.length > 0;
  const isFinished = game.status === 'finished';
  // For Exploding Kittens, winner is last player alive; for others, it's the player marked as winner
  const winner = isKittens 
    ? game.players.find(p => p.isAlive !== false && game.players.filter(pl => pl.isAlive !== false).length === 1)
    : game.players.find(p => p.isWinner);

  // UNO-specific state
  const currentPlayerIndex = game?.currentPlayerIndex ?? 0;
  const playerIndex = game?.players?.findIndex(p => p.id === currentPlayer?.id) ?? -1;
  const isMyTurn = (isUno || isKittens) && playerIndex === currentPlayerIndex;
  const topCard = isUno && game?.discardPile?.length > 0 ? game.discardPile[game.discardPile.length - 1] : null;
  const playableCardIndices = isUno && topCard && playerData?.cards 
    ? getPlayableCards(playerData.cards, topCard)
    : [];
  
  // Exploding Kittens state
  const isAlive = playerData?.isAlive !== false;
  const pendingDefuse = game?.pendingDefuse;

  const handleCardClick = async (card, cardIndex) => {
    if (!hasCards || isRemoving || loading) return;
    
    if (isUno) {
      // UNO card play logic
      if (!isMyTurn) {
        return;
      }
      
      if (!topCard) {
        return;
      }
      
      const isWildCard = card.type === 'wild' || card.action === 'wild' || card.action === 'wild_draw4';
      const canPlay = canPlayCard(card, topCard);
      
      // If it's not a wild card and can't be played, show error and return
      if (!isWildCard && !canPlay) {
        setLocalError('Cannot play this card! It must match the color, number, or symbol of the top card.');
        setTimeout(() => setLocalError(null), 3000); // Clear error after 3 seconds
        return;
      }
      
      // Clear any previous error
      setLocalError(null);
      
      // Handle wild cards
      if (isWildCard) {
        // For now, default to red if no color selected
        // In a full implementation, show a color picker modal
        const defaultColor = selectedWildColor || 'red';
        setIsRemoving(true);
        await playUnoCard(cardIndex, defaultColor);
        setSelectedWildColor(null);
        setTimeout(() => {
          setIsRemoving(false);
        }, 500);
      } else if (canPlay) {
        // Valid card that matches - play it
        setIsRemoving(true);
        await playUnoCard(cardIndex, null);
        setTimeout(() => {
          setIsRemoving(false);
        }, 500);
      }
    } else if (isKittens) {
      // Exploding Kittens card play logic
      if (!isMyTurn || !isAlive) {
        return;
      }
      
      // For now, allow playing single cards (action cards)
      // TODO: Add support for playing pairs
      if (card.type === 'action' || card.type === 'cat') {
        setIsRemoving(true);
        await playKittensCard([cardIndex]);
        setTimeout(() => {
          setIsRemoving(false);
        }, 500);
      } else {
        setLocalError('You can play action cards or pairs of cat cards');
        setTimeout(() => setLocalError(null), 3000);
      }
    } else {
      // Original billiards-style gameplay
      setIsRemoving(true);
      await potBall(card);
      
      setTimeout(() => {
        setIsRemoving(false);
      }, 500);
    }
  };
  
  const handleDrawCard = async () => {
    if ((!isUno && !isKittens) || !isMyTurn) return;
    if (isUno) {
      await drawUnoCard();
    } else if (isKittens) {
      await drawKittensCard();
    }
  };
  
  const handleCallUno = async () => {
    if (!isUno || !hasCards) return;
    if (playerData?.cards?.length === 1 && !playerData.hasCalledUno) {
      await callUno();
    }
  };
  
  const handlePlaceKitten = async (position) => {
    if (!isKittens || !pendingDefuse) return;
    await placeExplodingKitten(position);
  };

  // Show winner screen if game is finished
  if (isFinished && winner) {
    return (
      <WinnerScreen 
        winner={winner}
        isCurrentPlayer={winner.id === currentPlayer?.id}
        onPlayAgain={resetGame}
        onLeave={leaveGame}
      />
    );
  }

  return (
    <div className="game-page">
      <div className="game-container">
        {/* Header */}
        <header className="game-header">
          <div className="header-left">
            <span className="game-icon">{gameType.emoji}</span>
            <div className="game-info">
              <span className="game-code">Game: {game.id}</span>
              <span className="player-count">{game.players.length} players</span>
              {(isUno || isKittens) && (
                <span className="turn-indicator">
                  {isMyTurn ? '🟢 Your Turn' : `👤 ${game.players[currentPlayerIndex]?.name}'s Turn`}
                  {isKittens && !isAlive && <span style={{ color: '#dc2626', marginLeft: '8px' }}>💀 You Died</span>}
                </span>
              )}
            </div>
          </div>
          <button className="leave-btn" onClick={leaveGame}>
            ✕
          </button>
        </header>

        {/* UNO Discard Pile */}
        {isUno && (
          <section className="discard-section">
            <DiscardPile 
              topCard={topCard} 
              drawPileCount={game.drawPile?.length || 0}
            />
          </section>
        )}
        
        {/* Exploding Kittens Pending Defuse */}
        {isKittens && pendingDefuse && isMyTurn && (
          <section className="pending-defuse-section">
            <div className="pending-defuse-message">
              <h3>💣 Exploding Kitten!</h3>
              <p>Place it back in the deck (position 0 = top, {game.drawPile?.length || 0} = bottom)</p>
              <input 
                type="number" 
                min="0" 
                max={game.drawPile?.length || 0}
                defaultValue={Math.floor((game.drawPile?.length || 0) / 2)}
                id="kitten-position"
                style={{ padding: '8px', margin: '8px', borderRadius: '8px', width: '80px' }}
              />
              <button 
                onClick={() => {
                  const pos = parseInt(document.getElementById('kitten-position').value) || 0;
                  handlePlaceKitten(pos);
                }}
                className="draw-card-btn"
              >
                Place Kitten
              </button>
            </div>
          </section>
        )}

        {/* My Cards Section */}
        <section className="my-cards-section">
          <h2 className="section-title">
            <span className="my-name">{playerData?.name}</span>
            <span className="my-card-count">
              {playerData?.cards?.length || 0} {(isUno || isKittens) ? 'cards' : 'balls'}
              {isUno && playerData?.cards?.length === 1 && !playerData.hasCalledUno && (
                <span className="uno-warning"> - Call UNO!</span>
              )}
            </span>
          </h2>
          
          <CardHand 
            cards={playerData?.cards || []} 
            showCards={true} 
            onCardClick={handleCardClick}
            clickable={hasCards && !isRemoving && !loading && ((!isUno && !isKittens) || (isMyTurn && isAlive))}
            playableIndices={isUno ? playableCardIndices : []}
          />
          
          <div className="card-actions">
            {isUno ? (
              <>
                {isMyTurn && (
                  <>
                    {playableCardIndices.length === 0 ? (
                      <p className="tap-hint">No playable cards - you must draw</p>
                    ) : (
                      <p className="tap-hint">
                        {playableCardIndices.length} card{playableCardIndices.length !== 1 ? 's' : ''} playable
                      </p>
                    )}
                    <button 
                      className="draw-card-btn" 
                      onClick={handleDrawCard}
                      disabled={loading || isRemoving}
                    >
                      🃏 Draw Card
                    </button>
                    {playerData?.cards?.length === 1 && !playerData.hasCalledUno && (
                      <button 
                        className="uno-btn" 
                        onClick={handleCallUno}
                        disabled={loading}
                      >
                        🗣️ UNO!
                      </button>
                    )}
                  </>
                )}
                {!isMyTurn && (
                  <p className="tap-hint">Waiting for your turn...</p>
                )}
              </>
            ) : isKittens ? (
              <>
                {isMyTurn && isAlive && (
                  <>
                    {!pendingDefuse ? (
                      <>
                        <p className="tap-hint">Play cards, then draw</p>
                        <button 
                          className="draw-card-btn" 
                          onClick={handleDrawCard}
                          disabled={loading || isRemoving || game.skipDraw}
                        >
                          🃏 Draw Card
                        </button>
                      </>
                    ) : (
                      <p className="tap-hint">Place the exploding kitten back in the deck above</p>
                    )}
                  </>
                )}
                {!isMyTurn && isAlive && (
                  <p className="tap-hint">Waiting for your turn...</p>
                )}
                {!isAlive && (
                  <p className="tap-hint" style={{ color: '#dc2626' }}>💀 You exploded! Game over for you.</p>
                )}
              </>
            ) : (
              <>
                {hasCards && (
                  <p className="tap-hint">
                    {isRemoving ? 'Removing...' : '👆 Tap a ball to remove it'}
                  </p>
                )}
                {canUndo() && !loading && (
                  <button className="undo-btn" onClick={undoRemove}>
                    ↩️ Undo
                  </button>
                )}
              </>
            )}
          </div>
        </section>

        {/* Error Messages */}
        {(error || localError) && (
          <div className="error-message" style={{ margin: '16px 20px', padding: '12px 16px' }}>
            ⚠️ {error || localError}
          </div>
        )}

        {/* Removed Cards History */}
        {game.removedCards && game.removedCards.length > 0 && (
          <section className="history-section">
            <RemovedCardsHistory removedCards={game.removedCards} />
          </section>
        )}

        {/* Other Players */}
        <section className="players-section">
          <PlayerList 
            players={game.players} 
            currentPlayerId={currentPlayer?.id}
            currentPlayerIndex={(isUno || isKittens) ? currentPlayerIndex : undefined}
            gameType={gameTypeId}
          />
        </section>
      </div>
    </div>
  );
};

export default GamePage;


